import { useEffect, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useUsageSourceFormSchema } from "./useUsageSourceFormSchema";
import { useUsageSourceValidation } from "./useUsageSourceValidation";
import { useUsageSourceFormMutations } from "./useUsageSourceFormMutations";
import { formatUsageSourceSuccessMessage } from "../utils/usageSourceFormRules";

function getOriginValue(originSource) {
  return typeof originSource === "object" && originSource !== null ? originSource.value : originSource;
}

/**
 * State Management Layer - orchestrating hook, mirrors
 * _features/billingUnits/modals/BillingUnitFormModal/hooks/
 * useBillingUnitFormState.js's shape (dynamic-form engine + close-
 * confirmation dismiss guard + submit orchestration), PLUS wiring the
 * validation flow (see useUsageSourceValidation.js) and gating submit on
 * it exactly like the old modal did: blocks with a toast unless
 * `isValidated` or the origin is "API LINK".
 *
 * Owns `databaseOptions` directly (not delegated to useUsageSourceValidation)
 * because it must exist before useDynamicForm runs - see
 * useUsageSourceValidation.js's docstring for why.
 */
export function useUsageSourceFormState({ mode, rowData, isOpen, onClose, onSuccess, refetchFilters }) {
  const { showToast } = useToast();
  const { submitUsageSource, isSubmitting } = useUsageSourceFormMutations();

  const [databaseOptions, setDatabaseOptions] = useState(() =>
    mode === "edit" && rowData?.originSource?.value === "MongoDB" && rowData?.isValidated
      ? rowData?.availableDatabases || []
      : []
  );

  const rawSchema = useUsageSourceFormSchema({ databaseOptions });

  const dynamicForm = useDynamicForm(rawSchema, { mode, rowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    watch,
    handleChange,
    formState: { isDirty },
  } = dynamicForm;

  const originSourceValue = getOriginValue(watch("originSource"));

  const validation = useUsageSourceValidation({
    mode,
    rowData,
    originSourceValue,
    handleChange,
    databaseOptions,
    setDatabaseOptions,
  });

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setShowCloseConfirmation(false);
    onClose();
  }

  function attemptClose() {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    if (validation.requiresValidation && !validation.isValidated) {
      showToast("Please validate the source", "error");
      return;
    }

    try {
      const response = await submitUsageSource({ mode, validData, rowData });

      showToast(formatUsageSourceSuccessMessage(validData, mode), "success");
      onSuccess?.(response);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      showToast(error?.data?.message || "Something went wrong. Please try again.", "error");
    }
  });

  const handleValidate = (e) => {
    e.preventDefault();
    validation.validateUrl(dynamicForm.formData);
  };

  return {
    ...dynamicForm,
    schema: rawSchema,
    showCloseConfirmation,
    hasUnsavedChanges: isDirty,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
    originSourceValue,
    handleValidate,
    ...validation,
  };
}
